import { WCAG_22_AA_CRITERIA } from 'shared/constants/accessibility/wcag22Aa';

export const PAGE_TYPE = Object.freeze({
  ALL: 'ALL',
  HTML: 'HTML',
  PDF: 'PDF',
});

export const SCAN_SOURCE = Object.freeze({
  SQUIDLER: 'Squidler',
  EXTELLIO: 'Extellio',
});

export const SCAN_TYPE = Object.freeze({
  FULL_SCAN: 'Accessibility',
  PDF_SCAN: 'PDF scan',
  BROKEN_LINKS: 'Broken links check',
});

export const PAGE_COLUMN_KEYS = Object.freeze({
  ALL: ['type', 'page', 'issues', 'latestScan', 'scanType', 'evidence'],
  HTML: ['page', 'issues', 'latestScan', 'scanType', 'evidence'],
  PDF: ['page', 'issues', 'linkedFrom', 'latestPdfScanEvidence', 'evidence'],
});

export const PDF_SCAN_DISABLED_TEXT = 'Not scanned — PDF scanning disabled';
export const HISTORICAL_RESULTS_LABEL = 'Historical results — not a run';

export const DISCOVERY_KIND_LABELS = Object.freeze({
  LINK: 'Link',
  DOWNLOAD: 'Download',
  IFRAME: 'Iframe',
  EMBED: 'Embed',
  OBJECT: 'Object',
});

export const WCAG_CRITERIA_BY_ID = new Map(WCAG_22_AA_CRITERIA.map(criterion => [criterion.id, criterion]));

export function criteriaForIds(ids) {
  return (ids || []).map(id => WCAG_CRITERIA_BY_ID.get(id)).filter(Boolean);
}

export const EVIDENCE_STATUS_LABELS = Object.freeze({
  automated_failure: 'Automated issue found',
  manual_review_required: 'Manual check required',
  manual_pass: 'Manually passed',
  manual_fail: 'Manually failed',
  not_applicable: 'Not applicable',
  blocked: 'Blocked',
  not_started: 'No evidence yet',
});

export function evidenceStatusLabel(status) {
  return EVIDENCE_STATUS_LABELS[status] || status || 'Evidence status unavailable';
}

export function searchParamsFrom(search) {
  const raw = typeof search === 'string' && search.startsWith('?') ? search.slice(1) : (search || '');
  return new URLSearchParams(raw);
}

export function pageTypeFromSearch(search) {
  const value = searchParamsFrom(search).get('pageType');
  if (value === PAGE_TYPE.HTML || value === PAGE_TYPE.PDF) return value;
  return PAGE_TYPE.ALL;
}

export function includeHiddenDispositionsFromSearch(search) {
  return searchParamsFrom(search).get('hidden') === '1';
}

export function includeHiddenDispositionsSearch(includeHidden, currentSearch = '') {
  const params = searchParamsFrom(currentSearch);
  if (includeHidden) params.set('hidden', '1');
  else params.delete('hidden');
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function pageTypeSearch(pageType, currentSearch = '') {
  const params = searchParamsFrom(currentSearch);
  if (pageType === PAGE_TYPE.HTML || pageType === PAGE_TYPE.PDF) params.set('pageType', pageType);
  else params.delete('pageType');
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function pageTypeAppliesToPath(pathname = '') {
  return /\/(findings|pages|manual-review)(\/|$)/.test(pathname);
}

export function htmlOnlyFindingPath(pathname = '') {
  return /\/findings\/functionality(\/|$)/.test(pathname);
}

export function pageTypeForPath(pathname = '', pageType = PAGE_TYPE.ALL) {
  return htmlOnlyFindingPath(pathname) ? PAGE_TYPE.HTML : pageType;
}

export function pageTypeSyncSearch(pathname = '', pageType = PAGE_TYPE.ALL, currentSearch = '') {
  if (!pageTypeAppliesToPath(pathname)) return null;
  return pageTypeSearch(
    htmlOnlyFindingPath(pathname) ? PAGE_TYPE.ALL : pageType,
    currentSearch,
  );
}

export function pageTypeLabel(pageType) {
  if (pageType === PAGE_TYPE.PDF) return 'PDF';
  if (pageType === PAGE_TYPE.HTML) return 'HTML';
  return '—';
}

export function allIssueColumnKeys({ suiteType, pageType = PAGE_TYPE.ALL, includeIssueType = true, groupedByPage = false } = {}) {
  return [
    groupedByPage && 'page',
    includeIssueType && 'issueType',
    includeIssueType && suiteType === 'Accessibility' && 'providerRule',
    pageType === PAGE_TYPE.ALL && suiteType !== 'Functionality' && 'type',
    !groupedByPage && 'page',
    groupedByPage && 'findings',
    'status',
    'change',
    'lastObserved',
    'evidence',
  ].filter(Boolean);
}

export function groupOccurrencesByPage(occurrences = []) {
  const groups = new Map();
  occurrences.forEach((occurrence) => {
    const pageUrl = occurrence.pageUrl || '';
    const existing = groups.get(pageUrl);
    if (!existing) {
      groups.set(pageUrl, {
        id: `page:${pageUrl}`,
        pageUrl: occurrence.pageUrl || null,
        pageTitle: occurrence.pageTitle || null,
        pageType: occurrence.pageType,
        lastSeenAt: occurrence.lastSeenAt || null,
        detectionState: occurrence.detectionState,
        disposition: occurrence.disposition || null,
        dispositionMixed: false,
        findingCount: 1,
        occurrenceIds: [occurrence.id],
        occurrence,
      });
      return;
    }
    existing.findingCount += 1;
    existing.occurrenceIds.push(occurrence.id);
    existing.detectionState = null;
    if ((occurrence.disposition || null) !== existing.disposition) {
      existing.dispositionMixed = true;
      existing.disposition = null;
    }
    if (new Date(occurrence.lastSeenAt || 0).getTime() > new Date(existing.lastSeenAt || 0).getTime()) {
      existing.lastSeenAt = occurrence.lastSeenAt;
      existing.occurrence = occurrence;
    }
  });
  return [...groups.values()];
}

export function pageFromOccurrence(occurrence) {
  if (!occurrence) return null;
  return {
    id: occurrence.pageUrl || occurrence.id,
    pageUrl: occurrence.pageUrl,
    pageType: occurrence.pageType,
    pageTitle: occurrence.pageTitle || null,
    latestPdfScanEvidence: occurrence.pdfScanEvidenceId
      ? { id: occurrence.pdfScanEvidenceId }
      : null,
  };
}

export function defaultFindingSource(occurrence, groupSource) {
  if (occurrence?.source) return occurrence.source;
  if (occurrence?.pageType === PAGE_TYPE.PDF || String(occurrence?.evidenceKind || '').startsWith('PDF_')) {
    return 'Extellio PDF Scan';
  }
  return groupSource || 'Squidler Full Scan';
}

export function findingSourceLabel(source) {
  if (source === 'Squidler Full Scan') return 'Squidler · Full';
  if (source === 'Extellio PDF Scan') return 'Extellio · PDF';
  if (source === 'Extellio Broken Links Checker') return 'Extellio · Links';
  return source || '—';
}

export function suiteIssueCount(suite) {
  if (typeof suite?.occurrenceCount === 'number') return suite.occurrenceCount;
  return suite?.providerProblemCount || 0;
}

export const DASHBOARD_EVIDENCE_SOURCES = Object.freeze([
  { source: 'PROVIDER', label: 'Accessibility', findingsPath: '/findings/accessibility', pageType: PAGE_TYPE.HTML },
  { source: 'PDF', label: 'PDF', findingsPath: '/findings/accessibility', pageType: PAGE_TYPE.PDF },
  { source: 'BROKEN_LINKS', label: 'Broken links', findingsPath: '/findings/functionality' },
]);

export function scanOverviewSource(scanOverview, source) {
  return (scanOverview?.sources || []).find(item => item.source === source) || null;
}

export function overviewSourceFindingsPath(source, baseUrl) {
  const item = DASHBOARD_EVIDENCE_SOURCES.find(entry => entry.source === source);
  if (!item) return `${baseUrl}/findings/accessibility`;
  return `${baseUrl}${item.findingsPath}${item.pageType ? pageTypeSearch(item.pageType) : ''}`;
}

export function accessibilityCardStatistics(scanOverview) {
  if (!scanOverview?.sources) return undefined;
  return DASHBOARD_EVIDENCE_SOURCES.map(({ source, label }) => ({
    title: label,
    value: scanOverviewSource(scanOverview, source)?.observationCount || 0,
  }));
}

export function accessibilityCardLastScanAt(scanOverview) {
  const times = (scanOverview?.sources || [])
    .map(item => item.latestCompletedRun?.completedAt || item.latestCompletedRun?.createdAt)
    .filter(Boolean)
    .sort();
  return times[times.length - 1] || null;
}

export function isPdfEvidence(record) {
  return record?.pageType === PAGE_TYPE.PDF
    || record?.evidenceKind === 'PDF_RULE';
}

export function verifiedBoundingBox(box) {
  if (!box || typeof box !== 'object') return null;
  const x = Number(box.x);
  const y = Number(box.y);
  const width = Number(box.width);
  const height = Number(box.height);
  if (![x, y, width, height].every(Number.isFinite)) return null;
  if (width <= 0 || height <= 0) return null;
  return { x, y, width, height };
}

export function representativeUrl(unit) {
  return unit?.urls?.find(url => url.representative) || unit?.urls?.[0] || null;
}

export function representativePageType(unit) {
  return representativeUrl(unit)?.pageType || null;
}

export function reviewHasBothPageTypes(units = []) {
  const types = new Set(units.map(representativePageType).filter(Boolean));
  return types.has(PAGE_TYPE.HTML) && types.has(PAGE_TYPE.PDF);
}

export function effectiveManualReviewPageType(filterVisible, pageType = PAGE_TYPE.ALL) {
  return filterVisible ? (pageType || PAGE_TYPE.ALL) : PAGE_TYPE.ALL;
}

export function filterReviewUnits(units = [], pageType = PAGE_TYPE.ALL) {
  if (!pageType || pageType === PAGE_TYPE.ALL) return units;
  return units.filter(unit => representativePageType(unit) === pageType);
}

export function controlsForPageType(controls = [], pageType = null) {
  if (!pageType) return controls;
  return controls.filter((control) => {
    const applicable = control.applicablePageTypes;
    if (!applicable) return true;
    return applicable.includes(pageType);
  });
}

export function reviewUnitSummary(units = []) {
  const completed = units.filter(({ workStatus }) => workStatus === 'completed').length;
  const inProgress = units.filter(({ workStatus }) => workStatus === 'in_progress').length;
  const blocked = units.filter(({ workStatus }) => workStatus === 'blocked').length;
  return {
    completed,
    inProgress,
    blocked,
    remaining: units.length - completed,
  };
}

export function discoveryKindLabel(kind) {
  return DISCOVERY_KIND_LABELS[kind] || kind || '—';
}

export function pageColumnKeys(pageType = PAGE_TYPE.ALL) {
  return PAGE_COLUMN_KEYS[pageType] || PAGE_COLUMN_KEYS.ALL;
}

export function latestNonHistoricalScan(scanHistory = []) {
  return (scanHistory || []).find(row => row && !row.historicalResults) || null;
}

export function scanTypeIncludes(scanType, type) {
  if (!scanType || !type) return false;
  return scanType === type || String(scanType).split(', ').includes(type);
}

export function latestSquidlerFullScan(scanHistory = []) {
  return (scanHistory || []).find(row => (
    row
    && !row.historicalResults
    && scanTypeIncludes(row.scanType, SCAN_TYPE.FULL_SCAN)
  )) || null;
}

export function htmlLatestScanPresentation(scanHistory = []) {
  const scan = latestSquidlerFullScan(scanHistory);
  if (!scan) return { text: 'Not scanned', kind: 'none', scanType: SCAN_TYPE.FULL_SCAN };
  const status = typeof scan.status === 'string' ? scan.status.toLowerCase() : '';
  const at = scan.completedAt || scan.startedAt || null;
  if (status === 'completed' && at) {
    return { text: 'Scanned', kind: 'completed', completedAt: at, scanType: SCAN_TYPE.FULL_SCAN };
  }
  if (status === 'failed') return { text: 'Scan failed', kind: 'failed', scanType: SCAN_TYPE.FULL_SCAN };
  if (status === 'pending' || status === 'running') {
    return { text: 'Scanning', kind: 'pending', scanType: SCAN_TYPE.FULL_SCAN };
  }
  return { text: scan.status || 'Not scanned', kind: 'other', completedAt: at, scanType: SCAN_TYPE.FULL_SCAN };
}

export function pagesCheckedPresentation(row) {
  return Number.isInteger(row?.pagesChecked) ? row.pagesChecked : '';
}

export function scanReasonTriggerPresentation(row) {
  if (row?.historicalResults) return HISTORICAL_RESULTS_LABEL;
  if (row?.trigger === 'setting catch-up' || row?.reason?.startsWith('setting:')) {
    return 'PDF scanning enabled';
  }
  return row?.reason || row?.trigger || '—';
}

export function scanStatusPresentation(row) {
  if (row?.historicalResults) return 'Historical';
  return row?.status || '—';
}

export function pdfBatchProgressPresentation(row) {
  if (row?.historicalResults) return null;
  const progress = row?.pdfBatchProgress;
  if (!progress) return null;
  if (
    !scanTypeIncludes(row?.scanType, SCAN_TYPE.PDF_SCAN)
    && !scanTypeIncludes(row?.scanType, SCAN_TYPE.BROKEN_LINKS)
  ) return null;
  return `${progress.completed} completed, ${progress.failed} failed, ${progress.skipped} skipped, ${progress.queued} queued`;
}

export function pageLimitAlertPresentation({ pageLimit, htmlPageCount } = {}) {
  if (!Number.isInteger(pageLimit) || pageLimit < 0) return null;
  if (!Number.isInteger(htmlPageCount) || htmlPageCount <= pageLimit) return null;
  return {
    pageLimit,
    htmlPageCount,
    message: 'Accessibility license page limit',
    description: `This license includes ${pageLimit} HTML pages, so only the ${pageLimit} of ${htmlPageCount} inventoried HTML pages with the most findings are shown. Findings and related evidence use the same pages.`,
  };
}

export function pageCoveragePresentation({
  allAffectedPageCount,
  inventoriedPageCount,
} = {}) {
  return {
    pagesWithFindings: Number.isInteger(allAffectedPageCount) ? allAffectedPageCount : 0,
    inventoriedPages: Number.isInteger(inventoriedPageCount) ? inventoriedPageCount : null,
  };
}

export function overviewActivationPresentation({ licensed, canManage, canEditLicense, isSuperUser } = {}) {
  if (!licensed) {
    return {
      title: 'Accessibility is not included in the license',
      text: isSuperUser
        ? 'The active license does not include Accessibility, so scheduled scans stay off. Super users can still activate for manual checks.'
        : 'The active license does not include Accessibility, so this review cannot be activated.',
      action: canEditLicense ? 'upgrade' : (isSuperUser ? null : 'ask-owner'),
      secondaryAction: isSuperUser ? 'activate-anyway' : null,
    };
  }
  if (!canManage) {
    return {
      title: 'Accessibility review is not active',
      text: 'Ask someone with Accessibility edit permission to activate this review.',
      action: null,
      secondaryAction: null,
    };
  }
  return {
    title: 'Accessibility review is not active',
    text: 'Connect Squidler and enable weekly Accessibility, PDF, and Broken Links scans for this website.',
    action: 'activate',
    secondaryAction: null,
  };
}

export function nextScheduledScanAt(checkInterval, fromDate) {
  if (!checkInterval || !fromDate) return null;
  const date = new Date(fromDate);
  if (Number.isNaN(date.getTime())) return null;
  if (checkInterval === 'WEEK') {
    date.setUTCDate(date.getUTCDate() + 7);
    return date.toISOString();
  }
  if (checkInterval === 'MONTH') {
    date.setUTCMonth(date.getUTCMonth() + 1);
    return date.toISOString();
  }
  return null;
}

export function nextScheduledScanPresentation({ checkInterval, latestScanAt, now = Date.now() } = {}) {
  if (!checkInterval) return { text: 'Not scheduled' };
  if (!latestScanAt) return { text: 'Due' };
  const at = nextScheduledScanAt(checkInterval, latestScanAt);
  if (!at) return { text: 'Not scheduled' };
  if (Date.parse(at) <= now) return { text: 'Due' };
  return { text: null, at };
}

export function pdfScanPresentation(page, { pdfScanningEnabled } = {}) {
  if (page?.pageType !== PAGE_TYPE.PDF) return { text: '—', kind: 'na' };
  const scan = page.latestPdfScanEvidence;
  const skippedDisabled = !scan
    || scan.status === 'SKIPPED_DISABLED'
    || scan.errorCode === 'skipped_disabled';
  if (skippedDisabled && (pdfScanningEnabled === false || scan?.status === 'SKIPPED_DISABLED')) {
    return { text: PDF_SCAN_DISABLED_TEXT, kind: 'disabled' };
  }
  if (!scan) return { text: 'Not scanned', kind: 'pending' };
  if (scan.status === 'FAILED') {
    return {
      text: scan.errorCode ? `Scan failed (${scan.errorCode})` : 'Scan failed',
      kind: 'failed',
    };
  }
  if (scan.status === 'PENDING') return { text: 'Scanning', kind: 'pending' };
  if (scan.status === 'COMPLETED') {
    return { text: 'Scanned', kind: 'completed', completedAt: scan.completedAt || null };
  }
  return { text: scan.status, kind: 'other' };
}

export function latestScanPresentation(page, { scanHistory, pdfScanningEnabled } = {}) {
  if (page?.pageType === PAGE_TYPE.PDF) {
    return { ...pdfScanPresentation(page, { pdfScanningEnabled }), scanType: SCAN_TYPE.PDF_SCAN };
  }
  return htmlLatestScanPresentation(scanHistory);
}

export function isPdfUaFinding(group) {
  return String(group?.id || '').startsWith('pdf:')
    || String(group?.ruleId || '').startsWith('PDF/UA');
}

export function wcagCriteriaLabel(group) {
  return (group?.criterionIds || []).map((id) => {
    const criterion = WCAG_CRITERIA_BY_ID.get(id);
    return criterion ? `${id} ${criterion.title}` : id;
  }).join(', ');
}

export function findingEvidenceStatusLabel(group) {
  return evidenceStatusLabel(group?.status);
}

export const FINDING_DISPOSITION_LABELS = Object.freeze({
  UNTRIAGED: 'Untriaged',
  IN_PROGRESS: 'In progress',
  RESOLVED: 'Resolved',
  MUTED: 'Muted',
  DISMISSED: 'Dismissed',
});

export function findingDispositionOf(record) {
  if (!record) return null;
  if (Object.prototype.hasOwnProperty.call(record, 'disposition')) {
    return record.disposition || null;
  }
  return record.occurrence?.disposition || null;
}

export function findingDispositionLabel(value) {
  if (!value) return FINDING_DISPOSITION_LABELS.UNTRIAGED;
  return FINDING_DISPOSITION_LABELS[value] || value;
}

export function canSetFindingDisposition(record) {
  const occurrence = record?.occurrence || record;
  if (!occurrence?.providerProblemNumber) return false;
  if (occurrence.evidenceKind === 'PDF_RULE') return false;
  if (occurrence.source === 'Extellio Broken Links Checker') return false;
  return true;
}

export function findingOccurrenceIds(record) {
  if (record?.occurrenceIds?.length) return record.occurrenceIds.filter(Boolean);
  if (record?.occurrence?.id) return [record.occurrence.id];
  if (record?.id && record.providerProblemNumber) return [record.id];
  return [];
}

export function findingStandardLabel(group) {
  if (isPdfUaFinding(group)) return 'PDF/UA-1';
  if (group?.classification === 'provider_best_practice_observation') return 'Accessibility best practice';
  if (group?.criterionIds?.length) return 'WCAG 2.2';
  return 'Unclassified provider rule';
}

export function findingRequirementLabel(group) {
  const criteria = wcagCriteriaLabel(group);
  if (group?.classification === 'provider_best_practice_observation') {
    return criteria
      ? `Related WCAG ${group.criterionIds.length === 1 ? 'criterion' : 'criteria'}: ${criteria}`
      : 'No direct WCAG criterion';
  }
  if (criteria) {
    return isPdfUaFinding(group)
      ? `Related WCAG ${group.criterionIds.length === 1 ? 'criterion' : 'criteria'}: ${criteria}`
      : criteria;
  }
  if (isPdfUaFinding(group)) {
    const technicalRule = String(group.ruleId || '').replace(/^PDF\/UA\s*/, '');
    return technicalRule ? `Technical requirement ${technicalRule}` : 'PDF/UA technical requirement';
  }
  return 'No mapped requirement';
}

export function findingReviewPresentation(group) {
  const standard = findingStandardLabel(group);
  const criteria = wcagCriteriaLabel(group);
  const status = group?.status || null;
  if (group?.classification === 'provider_best_practice_observation') {
    return {
      primary: standard,
      status,
      context: criteria ? `Related to WCAG 2.2 — ${criteria}` : 'No direct WCAG criterion',
    };
  }
  if (isPdfUaFinding(group)) {
    const technicalRule = String(group?.ruleId || '').replace(/^PDF\/UA\s*/, '');
    return {
      primary: technicalRule ? `${standard} — requirement ${technicalRule}` : standard,
      status,
      context: criteria ? `Related to WCAG 2.2 — ${criteria}` : null,
    };
  }
  if (criteria) {
    return {
      primary: `${standard} — ${criteria}`,
      status,
      context: null,
    };
  }
  return { primary: standard, status, context: 'No mapped requirement' };
}

export function findingHeadingLabel(group) {
  return findingReviewPresentation(group).primary;
}

export function distinctPdfLocations(locations = [], canonicalUrl = '') {
  return (locations || []).filter((location) => {
    const requested = location?.requestedUrl || '';
    const final = location?.finalUrl || '';
    if (requested && requested !== canonicalUrl) return true;
    if (final && final !== requested && final !== canonicalUrl) return true;
    return false;
  });
}

export const DOCUMENT_PREVIEW_LABEL = 'Document preview';
export const PAGE_EVIDENCE_LABEL = 'Page evidence';
export const VISUAL_EVIDENCE_LABEL = 'Visual evidence';
export const DOCUMENT_LEVEL_FINDING_MESSAGE = 'This is a document-level finding; no visual location is available.';

export function positiveDocumentPageNumber(value) {
  return Number.isInteger(value) && value > 0 ? value : null;
}

export function pdfFindingEvidenceLabel(occurrence) {
  const page = positiveDocumentPageNumber(occurrence?.documentPageNumber);
  return page ? `Page ${page} preview` : null;
}

export function pdfFindingPanelTitle(occurrence) {
  return pdfFindingEvidenceLabel(occurrence) || VISUAL_EVIDENCE_LABEL;
}

export function pdfFindingScreenshot(occurrence) {
  const page = positiveDocumentPageNumber(occurrence?.documentPageNumber);
  const screenshot = occurrence?.screenshot;
  if (!page || !screenshot?.storageUrl) return null;
  const screenshotPage = positiveDocumentPageNumber(screenshot.documentPageNumber);
  if (screenshotPage !== page) return null;
  return screenshot;
}

export function pageEvidenceScreenshot({
  pageType,
  occurrences = [],
  websiteCheckScreenshot,
  scanPreview,
  focusedOccurrence,
} = {}) {
  if (pageType === PAGE_TYPE.PDF) {
    const exactPage = pdfFindingScreenshot(focusedOccurrence);
    if (exactPage) return exactPage;
    if (positiveDocumentPageNumber(focusedOccurrence?.documentPageNumber)) return null;
    return (scanPreview?.storageUrl && scanPreview) || null;
  }
  return websiteCheckScreenshot
    || occurrences
      .map(occurrence => occurrence.screenshot)
      .find(screenshot => screenshot?.storageUrl && screenshot.source === 'extellio_locator_preview')
    || occurrences.find(occurrence => occurrence.screenshot?.storageUrl)?.screenshot
    || null;
}

function visibleFindingSuites(suites) {
  return (suites || []).filter(suite => suite.suiteType && suite.suiteType !== 'Language');
}

export function occurrencesForInventoryPage(result, page) {
  if (!page) return [];
  const urls = new Set([page.pageUrl, ...(page.locations || []).flatMap(location => [
    location.requestedUrl,
    location.finalUrl,
  ])].filter(Boolean));
  const groups = result?.findingSuites
    ? visibleFindingSuites(result.findingSuites).flatMap(suite => (suite.findingGroups || []).map(group => ({
      ...group,
      suiteType: suite.suiteType,
    })))
    : (result?.findingGroups || []);
  return groups.flatMap(group => (group.occurrences || [])
    .filter(occurrence => urls.has(occurrence.pageUrl))
    .map(occurrence => ({
      ...occurrence,
      ruleId: group.ruleId,
      criterionIds: group.criterionIds,
      classification: group.classification,
      status: occurrence.status || group.status,
      suiteType: group.suiteType || 'Accessibility',
    })));
}

function occurrenceValueKey(value) {
  if (value == null || value === '') return null;
  if (Array.isArray(value)) return value.length ? JSON.stringify(value) : null;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function uniqueOccurrenceValues(occurrences, getValue) {
  const seen = new Map();
  (occurrences || []).forEach((occurrence) => {
    const value = getValue(occurrence);
    const key = occurrenceValueKey(value);
    if (key == null || seen.has(key)) return;
    seen.set(key, value);
  });
  return [...seen.values()];
}

export function pageEvidenceFindingGroups(occurrences = []) {
  const groups = new Map();
  occurrences.forEach((occurrence) => {
    const key = `${occurrence.suiteType || ''}::${occurrence.ruleId || occurrence.id}`;
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, {
        id: key,
        suiteType: occurrence.suiteType,
        ruleId: occurrence.ruleId,
        criterionIds: occurrence.criterionIds,
        classification: occurrence.classification,
        status: occurrence.status,
        pageType: occurrence.pageType,
        source: occurrence.source,
        occurrences: [occurrence],
      });
      return;
    }
    existing.occurrences.push(occurrence);
  });
  return [...groups.values()].map(group => ({ ...group, count: group.occurrences.length }));
}

const GROUPED_DETAIL_FIELDS = [
  ['description', occurrence => occurrence.description || null],
  ['brokenLink', (occurrence) => {
    if (!occurrence.linkUrl) return null;
    return { linkUrl: occurrence.linkUrl, httpStatus: occurrence.httpStatus ?? null };
  }],
  ['affectedHtml', occurrence => occurrence.affectedHtml || null],
  ['cssSelectors', (occurrence) => {
    const selectors = occurrence.cssSelectors || [];
    return selectors.length ? selectors : null;
  }],
  ['documentPageNumber', occurrence => occurrence.documentPageNumber ?? null],
  ['fixInstructions', occurrence => occurrence.fixInstructions || null],
  ['helpUrl', occurrence => occurrence.helpUrl || null],
];

export function groupedFindingDetails(occurrences = []) {
  const shared = {};
  const listed = {};
  GROUPED_DETAIL_FIELDS.forEach(([key, getValue]) => {
    const values = uniqueOccurrenceValues(occurrences, getValue);
    if (values.length === 1) {
      const [value] = values;
      shared[key] = value;
    } else if (values.length > 1) listed[key] = values;
  });
  return { shared, listed };
}

export function inventoryPageIssueCount(result, page) {
  return occurrencesForInventoryPage(result, page).length;
}

export function inventoryPageTitle(result, page) {
  if (page?.pageType !== PAGE_TYPE.HTML) return null;
  const urls = new Set([page.pageUrl, ...(page.locations || []).flatMap(location => [
    location.requestedUrl,
    location.finalUrl,
  ])].filter(Boolean).map(url => url.replace(/\/+$/, '')));
  const groups = result?.findingSuites
    ? visibleFindingSuites(result.findingSuites).flatMap(suite => suite.findingGroups || [])
    : (result?.findingGroups || []);
  return groups.flatMap(group => group.occurrences || [])
    .find(occurrence => occurrence.pageTitle && urls.has(occurrence.pageUrl?.replace(/\/+$/, '')))
    ?.pageTitle || null;
}

export function inventoryTableRows(inventory, result) {
  return (inventory?.pages || []).map((page) => {
    const occurrences = occurrencesForInventoryPage(result, page);
    return {
      ...page,
      canonicalUrl: page.pageUrl || page.locations?.[0]?.requestedUrl || page.locations?.[0]?.finalUrl || null,
      pageTitle: inventoryPageTitle(result, page),
      linkedFromCount: page.pageType === PAGE_TYPE.PDF ? page.referringPageCount : null,
      issueCount: occurrences.length,
    };
  });
}

export function mergeInventoryPages(current, incoming, key) {
  if (!incoming) return current;
  const base = current?.key === key ? (current.pages || []) : [];
  const pages = [...new Map([...base, ...(incoming.pages || [])].map(page => [page.id, page])).values()];
  return {
    ...incoming,
    key,
    pages,
  };
}

export function applicablePageTypesLabel(types) {
  if (!types || !types.length) return 'HTML, PDF';
  return [...new Set(types)].join(', ');
}
